import { useState, useEffect } from 'react';
import { Users, ShoppingCart, Coins, AlertTriangle, Award, Trophy, Medal } from 'lucide-react';
import { API_URL } from '../../config/api';

const CustomerAnalysis = ({ getDateRange, reportStoreFilter, formatCurrency }) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sortField, setSortField] = useState('total_revenue');
  const [sortDir, setSortDir] = useState('desc');

  useEffect(() => {
    const fetch_ = async () => {
      setLoading(true);
      try {
        const dr = getDateRange();
        let params = `start_date=${dr.start}&end_date=${dr.end}`;
        if (reportStoreFilter === 'gold') params += '&store_id=1';
        else if (reportStoreFilter === 'silver') params += '&store_ids=2,3';
        else if (reportStoreFilter !== 'all') params += `&store_id=${reportStoreFilter}`;

        const res = await fetch(`${API_URL}/reports/customer-analysis?${params}`);
        if (res.ok) setData(await res.json());
      } catch (e) { console.error(e); }
      finally { setLoading(false); }
    };
    fetch_();
  }, [getDateRange, reportStoreFilter]);

  if (loading) return <div className="empty-state"><div className="empty-icon">⏳</div><p>Müşteri verileri yükleniyor...</p></div>;

  const totalCustomers = data.length;
  const vipCustomers = data.filter(c => c.purchase_count >= 5);
  const totalRevenue = data.reduce((s, c) => s + parseFloat(c.total_revenue || 0), 0);
  const totalDebt = data.reduce((s, c) => s + (c.debt_balance || 0), 0);
  const avgBasket = totalCustomers > 0 ? totalRevenue / data.reduce((s, c) => s + c.purchase_count, 0) : 0;

  const sorted = [...data].sort((a, b) => {
    const av = parseFloat(a[sortField]) || 0;
    const bv = parseFloat(b[sortField]) || 0;
    return sortDir === 'desc' ? bv - av : av - bv;
  });

  const handleSort = (field) => {
    if (sortField === field) setSortDir(d => d === 'desc' ? 'asc' : 'desc');
    else { setSortField(field); setSortDir('desc'); }
  };

  const top10 = sorted.slice(0, 10);
  const maxRevenue = Math.max(...top10.map(c => parseFloat(c.total_revenue) || 0), 1);

  return (
    <div className="tab-content">
      <div className="stats-grid">
        <div className="stat-card primary">
          <div className="stat-icon"><Users size={20} /></div>
          <div className="stat-info">
            <div className="stat-value">{totalCustomers}</div>
            <div className="stat-label">Toplam Müşteri</div>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon"><Award size={20} style={{ color: '#0f766e' }} /></div>
          <div className="stat-info">
            <div className="stat-value">{vipCustomers.length}</div>
            <div className="stat-label">VIP Müşteri (5+ alım)</div>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon"><ShoppingCart size={20} style={{ color: '#0f766e' }} /></div>
          <div className="stat-info">
            <div className="stat-value">{formatCurrency(avgBasket)}</div>
            <div className="stat-label">Ort. Sepet Tutarı</div>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon"><AlertTriangle size={20} style={{ color: '#e74c3c' }} /></div>
          <div className="stat-info">
            <div className="stat-value">{formatCurrency(totalDebt)}</div>
            <div className="stat-label">Toplam Borç</div>
          </div>
        </div>
      </div>

      {/* Top 10 Müşteri Bar Chart */}
      <div className="chart-card">
        <h3>En Değerli 10 Müşteri</h3>
        <div className="horizontal-bars">
          {top10.map((c, i) => (
            <div key={c.customer_id} className="h-bar-item">
              <div className="h-bar-label">
                <span className="h-bar-rank">
                  {i === 0 ? <Trophy size={14} style={{ color: '#f59e0b' }} /> :
                   i === 1 ? <Medal size={14} style={{ color: '#94a3b8' }} /> :
                   i === 2 ? <Medal size={14} style={{ color: '#b45309' }} /> :
                   <span style={{ color: '#5a6c7d', fontSize: '12px' }}>{i + 1}</span>}
                </span>
                <span className="h-bar-name">{c.name || 'İsimsiz'}</span>
                <span className="h-bar-value">{formatCurrency(c.total_revenue)}</span>
              </div>
              <div className="h-bar-track">
                <div className={`h-bar-fill color-${(i % 5) + 1}`} style={{ width: `${(parseFloat(c.total_revenue) / maxRevenue) * 100}%` }} />
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Müşteri Tablosu */}
      <div className="data-card">
        <h3>Müşteri Detayları</h3>
        <div className="table-wrapper">
          <table className="data-table">
            <thead>
              <tr>
                <th>#</th>
                <th>Müşteri</th>
                <th>Telefon</th>
                <th className="sortable" onClick={() => handleSort('purchase_count')}>
                  Alım Sayısı {sortField === 'purchase_count' && (sortDir === 'desc' ? '↓' : '↑')}
                </th>
                <th className="sortable" onClick={() => handleSort('total_revenue')}>
                  Toplam Ciro {sortField === 'total_revenue' && (sortDir === 'desc' ? '↓' : '↑')}
                </th>
                <th className="sortable" onClick={() => handleSort('avg_basket')}>
                  Ort. Sepet {sortField === 'avg_basket' && (sortDir === 'desc' ? '↓' : '↑')}
                </th>
                <th>Son Alım</th>
                <th className="sortable" onClick={() => handleSort('debt_balance')}>
                  Borç {sortField === 'debt_balance' && (sortDir === 'desc' ? '↓' : '↑')}
                </th>
              </tr>
            </thead>
            <tbody>
              {sorted.slice(0, 100).map((c, i) => (
                <tr key={c.customer_id}>
                  <td>{i + 1}</td>
                  <td>
                    <strong>{c.name || 'İsimsiz'}</strong>
                    {c.purchase_count >= 5 && <span className="days-badge" style={{ background: '#f59e0b', color: '#fff', marginLeft: 6 }}>VIP</span>}
                  </td>
                  <td>{c.phone || '-'}</td>
                  <td><strong>{c.purchase_count}</strong></td>
                  <td><strong>{formatCurrency(c.total_revenue)}</strong></td>
                  <td>{formatCurrency(c.avg_basket)}</td>
                  <td>{c.last_purchase ? new Date(c.last_purchase).toLocaleDateString('tr-TR') : '-'}</td>
                  <td style={{ color: c.debt_balance > 0 ? '#e74c3c' : '#2ecc71' }}>
                    {c.debt_balance > 0 ? formatCurrency(c.debt_balance) : '-'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default CustomerAnalysis;
